/* P0 · Deal Rooms · Data + Readiness Consistency (2026-02-28)
 * ============================================================
 * Canonical Pipeline Value derivation.
 *
 * Every surface that shows a "Pipeline Value" number MUST call
 * `computePipelineValue(deals)` from here.  Duplicating the
 * eligibility filter + price parsing at each call site is what
 * previously drove three different Pipeline totals across the
 * Sidebar, Deal Rooms list KPI, and dashboard KPIs.
 *
 * Doctrine
 * --------
 *   • Eligible: LIVE pre-exchange deals only.  Excluded:
 *       - `archived` deals
 *       - `stage === "exchanged"` (money already banked from the
 *         pipeline's perspective — surfaced in the Exchanged
 *         KPI instead)
 *       - `stage === "completed"`
 *       - `status ∈ {"Exchanged", "Completed", "Fallen Through"}`
 *     This exactly matches the sub-tab filter operators see when
 *     they scan the "Active / Needs Attention" band on the Deal
 *     Rooms list — so the KPI number reconciles row-for-row.
 *
 *   • Price parsing accepts BOTH shapes carried by the current
 *     data model:
 *       - `d.agreedPriceAmount` (number, pilot canonical)
 *       - `d.agreedPrice` as a number
 *       - `d.agreedPrice` as a formatted string ("£745,000")
 *       - `d.value` / `d.askingPrice` as legacy fallbacks
 *     Missing / unparseable prices contribute 0 (never NaN).
 *
 *   • Deterministic + idempotent: same array in → same number
 *     out, byte-for-byte.  Ordering of the input list does not
 *     matter.
 *
 *   • No hard-coded totals.  No demo overrides.  If the pilot
 *     workspace shifts by £1, the KPI shifts by £1.
 */

const _NUMERIC_RE = /[^0-9.]/g;

/** Parse a stored price value into integer pounds.  Accepts:
 *  - number (returned as-is, floored)
 *  - "£745,000", "£745,000.00", "745000" → 745000
 *  - "£1,250,000.00" → 1250000
 *  - null / undefined / "" / "£0" / non-numeric → 0
 */
export function numFromPrice(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Math.max(0, Math.floor(v));
  const cleaned = String(v).replace(_NUMERIC_RE, "");
  if (!cleaned || cleaned === ".") return 0;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Deterministic eligibility filter — a deal contributes to the
 *  Pipeline Value if and only if it is a live pre-exchange row.
 */
export function isPipelineEligible(d) {
  if (!d) return false;
  if (d.archived) return false;
  const stage = String(d.stage || "").toLowerCase();
  if (stage === "exchanged" || stage === "completed") return false;
  const status = String(d.status || "");
  if (status === "Exchanged" || status === "Completed" || status === "Fallen Through") {
    return false;
  }
  return true;
}

/** Sum the agreed price of every eligible deal.  Never invents a
 *  value — a deal that carries no parseable price contributes 0.
 */
export function computePipelineValue(deals) {
  if (!Array.isArray(deals)) return 0;
  let total = 0;
  for (const d of deals) {
    if (!isPipelineEligible(d)) continue;
    total += numFromPrice(d.agreedPriceAmount)
      || numFromPrice(d.agreedPrice)
      || numFromPrice(d.value)
      || numFromPrice(d.askingPrice);
  }
  return total;
}

/** Reconciliation helper — returns the count of eligible deals AND
 *  the summed value in one pass, so a caller can render both the
 *  KPI value + its underlying subset size without a second walk.
 *  Used by lock tests + the KPI tooltip that reads "N deals · £Xm".
 */
export function computePipelineBreakdown(deals) {
  if (!Array.isArray(deals)) return { count: 0, value: 0 };
  let count = 0;
  let value = 0;
  for (const d of deals) {
    if (!isPipelineEligible(d)) continue;
    count += 1;
    value += numFromPrice(d.agreedPriceAmount)
      || numFromPrice(d.agreedPrice)
      || numFromPrice(d.value)
      || numFromPrice(d.askingPrice);
  }
  return { count, value };
}
